// Starting points for new boards.
import { generateNKeysBetween } from "fractional-indexing";
import type {
  BoardBackground, CardElement, FrameElement, LineElement, NoteElement, PageElement, PageSizeKey, SepiaElement, ShapeElement, StickerElement,
  TextElement
} from "../../shared/model";
import { PAGE_SIZES } from "../../shared/model";
import { newId, newSeed, routeConnector } from "../editor/elements";
import { stickerInfo } from "./stickers";

type Partial2<T> = Omit<T, "id" | "z" | "t" | "rot" | "type"> & { rot?: number };

const note = (x: number, y: number, text: string, paper = "#f6dc8f", tape: string | null = "#d98c8c", size = 200, rot = 0): Partial2<NoteElement> & { type: "note" } => ({
  type: "note", x, y, w: size, h: size, rot, text, paper, font: "hand", fontSize: 30, textColor: "#2b211a", align: "left", tape, shadow: true
});

const text = (x: number, y: number, t: string, fontSize = 28, font: TextElement["font"] = "hand", color = "#2b211a"): Partial2<TextElement> & { type: "text" } => ({
  type: "text", x, y, w: Math.max(40, t.length * fontSize * 0.45), h: fontSize * 1.3, text: t, font, fontSize, color, align: "left", autoW: true
});

const shape = (x: number, y: number, w: number, h: number, kind: ShapeElement["kind"], label = "", fill = "#fbf3e2", fillStyle: ShapeElement["fillStyle"] = "solid"): Partial2<ShapeElement> & { type: "shape" } => ({
  type: "shape", x, y, w, h, kind, stroke: "#2b211a", fill, fillStyle, strokeWidth: 2.2, dash: "solid", rough: 0.35, seed: newSeed(), text: label,
  font: "hand", fontSize: 28, align: "center", textColor: "#2b211a", shadow: false
});

const card = (x: number, y: number, w: number, h: number, md: string, accent = "#c2583a", paper = "#fbf6ea"): Partial2<CardElement> & { type: "card" } => ({
  type: "card", x, y, w, h, md, paper, accent, fontSize: 17, shadow: true
});

const frame = (x: number, y: number, w: number, h: number, title: string, color = "#d9b25a"): Partial2<FrameElement> & { type: "frame" } => ({
  type: "frame", x, y, w, h, title, color
});

const sticker = (x: number, y: number, key: string, side = 120, rot = 0): Partial2<StickerElement> & { type: "sticker" } => {
  const info = stickerInfo(key) ?? { w: 1, h: 1 };
  const k = side / Math.max(info.w, info.h);
  return { type: "sticker", x, y, w: info.w * k, h: info.h * k, key, rot };
};

const arrow = (from: string, to: string, curve = false): { type: "line"; from: string; to: string; curve: boolean } => ({ type: "line", from, to, curve });

type Spec = (ReturnType<typeof note> | ReturnType<typeof text> | ReturnType<typeof shape> | ReturnType<typeof card> | ReturnType<typeof frame> | ReturnType<typeof sticker>) & { ref?: string };

/** Turns template specs into real elements with ids, stacking order and attached connectors. */
function build(specs: (Spec | ReturnType<typeof arrow>)[]): SepiaElement[] {
  const now = Date.now();
  const byKey = new Map<string, SepiaElement>();
  const out: SepiaElement[] = [];
  const frames = specs.filter((s) => s.type === "frame");
  const rest = specs.filter((s) => s.type !== "frame");
  const ordered = [...frames, ...rest];
  const zs = generateNKeysBetween(null, null, ordered.length);
  ordered.forEach((s, i) => {
    if (s.type === "line") return;
    const { ref, ...spec } = s as Spec;
    const el = { ...spec, rot: spec.rot ?? 0, id: newId(), z: zs[i], t: now } as SepiaElement;
    if (ref) byKey.set(ref, el);
    out.push(el);
  });
  ordered.forEach((s, i) => {
    if (s.type !== "line") return;
    const a = byKey.get(s.from);
    const b = byKey.get(s.to);
    if (!a || !b) return;
    const ca = { x: a.x + a.w / 2, y: a.y + a.h / 2 };
    const cb = { x: b.x + b.w / 2, y: b.y + b.h / 2 };
    let line: LineElement = {
      id: newId(), type: "line", x: Math.min(ca.x, cb.x), y: Math.min(ca.y, cb.y), w: 1, h: 1, rot: 0, z: zs[i], t: now,
      pts: [[ca.x - Math.min(ca.x, cb.x), ca.y - Math.min(ca.y, cb.y)], [cb.x - Math.min(ca.x, cb.x), cb.y - Math.min(ca.y, cb.y)]],
      route: s.curve ? "curve" : "straight", start: { bind: a.id, ax: 0.5, ay: 0.5, head: "none" }, end: { bind: b.id, ax: 0.5, ay: 0.5, head: "arrow" },
      stroke: "#5b4636", strokeWidth: 2, dash: "solid", rough: 0.4, seed: newSeed(), label: "", font: "hand", fontSize: 20
    };
    if (s.curve) {
      const mid: [number, number] = [(line.pts[0][0] + line.pts[1][0]) / 2 + (cb.y - ca.y) * 0.15, (line.pts[0][1] + line.pts[1][1]) / 2 - (cb.x - ca.x) * 0.15];
      line = { ...line, pts: [line.pts[0], mid, line.pts[1]] };
    }
    out.push(routeConnector(line, (id) => out.find((e) => e.id === id)));
  });
  return out;
}

export interface Template {
  key: string;
  title: string;
  blurb: string;
  bg: BoardBackground;
  make: () => SepiaElement[];
}

export const TEMPLATES: Template[] = [
  { key: "blank", title: "Blank quadrille", blurb: "An empty grid, ready for anything", bg: { paper: "cream", pattern: "grid", size: 24 }, make: () => [] },
  {
    key: "welcome",
    title: "A little tour",
    blurb: "What Sepia can do, on one page",
    bg: { paper: "cream", pattern: "grid", size: 24 },
    make: () =>
      build([
        { ...text(-40, -300, "Welcome to your sketchbook", 64, "hand"), ref: "t" },
        text(-36, -222, "Everything here can be moved, restyled, linked and erased. Try it!", 22, "print", "#5b4636"),
        { ...note(-40, -140, "Draw with the Pencil.\nFingers pan & pinch.", "#f6dc8f", "#d98c8c", 220, -0.03), ref: "n1" },
        { ...note(230, -130, "Hold at the end of a stroke to snap a perfect shape", "#cfdcb6", "#8aa3bf", 220, 0.025), ref: "n2" },
        { ...note(500, -140, "Scribble over ink to erase it", "#f3c0bb", "#d9b25a", 220, -0.02), ref: "n3" },
        { ...card(-40, 150, 380, 300, "# Index cards\nWrite **markdown** here.\n- [ ] tap boxes to tick them\n- link boards: [[My first board]]\n- math: $e^{i\\pi} + 1 = 0$"), ref: "c" },
        { ...shape(430, 170, 220, 130, "roundrect", "Shapes & arrows\nstay connected"), ref: "s1" },
        { ...shape(470, 380, 170, 110, "diamond", "decide?", "#f4d98c"), ref: "s2" },
        arrow("s1", "s2"),
        sticker(700, 120, "bulb", 110, 0.1),
        sticker(710, 360, "cuttlefish", 130, -0.08),
        text(690, 500, "← two-finger tap = undo", 24, "hand", "#7a4a2a")
      ])
  },
  {
    key: "brainstorm",
    title: "Brainstorm",
    blurb: "A big question and room for ideas",
    bg: { paper: "cream", pattern: "dots", size: 24 },
    make: () => {
      const cx = 0;
      const cy = 0;
      const specs: (Spec | ReturnType<typeof arrow>)[] = [{ ...shape(cx - 170, cy - 90, 340, 180, "cloud", "Big question?", "#f4d98c"), ref: "q" }];
      const papers = ["#f6dc8f", "#f5c49a", "#cfdcb6", "#c4d9e4", "#dacdea", "#f3c0bb"];
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2 - Math.PI / 2;
        const key = "i" + i;
        specs.push({ ...note(cx + Math.cos(a) * 430 - 100, cy + Math.sin(a) * 300 - 100, "idea…", papers[i], i % 2 ? "#93ad86" : "#d98c8c", 200, (i % 2 ? 1 : -1) * 0.03), ref: key });
        specs.push(arrow("q", key, true));
      }
      specs.push(sticker(cx + 150, cy - 200, "bulb", 100, 0.15));
      return build(specs);
    }
  },
  {
    key: "cornell",
    title: "Cornell notes",
    blurb: "Cues, notes and a summary",
    bg: { paper: "white", pattern: "lines", size: 24 },
    make: () =>
      build([
        frame(0, 0, 900, 1100, "Lecture notes"),
        text(24, 20, "Topic:", 40, "hand"),
        text(640, 30, "Date:", 26, "hand", "#5b4636"),
        shape(20, 110, 240, 780, "rect", "", "#f3e6cf", "none"),
        text(40, 124, "Cues & questions", 26, "hand", "#7a4a2a"),
        text(290, 124, "Notes", 26, "hand", "#7a4a2a"),
        shape(20, 910, 860, 170, "rect", "", "#f3e6cf", "none"),
        text(40, 924, "Summary", 26, "hand", "#7a4a2a")
      ])
  },
  {
    key: "kanban",
    title: "Kanban",
    blurb: "To do, doing, done",
    bg: { paper: "kraft", pattern: "grid", size: 24 },
    make: () =>
      build([
        frame(0, 0, 330, 760, "To do", "#d0845f"),
        frame(370, 0, 330, 760, "Doing", "#d9b25a"),
        frame(740, 0, 330, 760, "Done", "#93ad86"),
        note(40, 40, "First task", "#f6dc8f", "#d98c8c", 240, -0.02),
        note(60, 300, "Another one", "#f5c49a", null, 240, 0.02),
        note(410, 40, "In progress…", "#c4d9e4", "#8aa3bf", 240, 0.015),
        sticker(890, 60, "done-stamp", 160, -0.12)
      ])
  },
  {
    key: "week",
    title: "Weekly planner",
    blurb: "Seven days and a list of goals",
    bg: { paper: "cream", pattern: "dots", size: 24 },
    make: () => {
      const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
      const accents = ["#c2583a", "#cf9234", "#4f8a5b", "#2f7c79", "#3f64a0", "#7d4a86", "#c5507a"];
      const specs: Spec[] = days.map((d, i) => card((i % 4) * 330, Math.floor(i / 4) * 330, 300, 300, `# ${d}\n- [ ] `, accents[i]));
      specs.push(card(3 * 330, 330, 300, 300, "# Goals this week\n- [ ] \n- [ ] \n- [ ] ", "#6f6760", "#f3e6cf"));
      specs.push(text(0, -90, "This week", 56, "hand"));
      specs.push(sticker(3 * 330 + 180, -110, "alarm-clock", 100, 0.1));
      return build(specs);
    }
  },
  {
    key: "mindmap",
    title: "Mind map",
    blurb: "One idea branching out",
    bg: { paper: "white", pattern: "blank", size: 24 },
    make: () => {
      const specs: (Spec | ReturnType<typeof arrow>)[] = [{ ...shape(-130, -60, 260, 120, "ellipse", "Central idea", "#f4d98c"), ref: "c" }];
      const fills = ["#bcd3df", "#c9d8b0", "#efb9b4", "#d5c8e6"];
      const pos = [[-520, -260], [300, -260], [-520, 180], [300, 180]];
      pos.forEach(([x, y], i) => {
        specs.push({ ...shape(x, y, 220, 90, "roundrect", "Branch " + (i + 1), fills[i]), ref: "b" + i });
        specs.push(arrow("c", "b" + i, true));
      });
      return build(specs);
    }
  },
  {
    key: "paper",
    title: "Paper reading",
    blurb: "Question, method, findings, your thoughts",
    bg: { paper: "cream", pattern: "grid", size: 24 },
    make: () =>
      build([
        text(0, -110, "Paper title", 52, "hand"),
        text(4, -40, "Authors · venue · year · link", 22, "print", "#5b4636"),
        card(0, 20, 380, 260, "# Question\nWhat problem does it solve, and why does it matter?", "#3f64a0"),
        card(410, 20, 380, 260, "# Method\nHow do they do it? Key assumptions.", "#4f8a5b"),
        card(0, 310, 380, 260, "# Findings\n- \n- ", "#cf9234"),
        card(410, 310, 380, 260, "# My thoughts\nStrengths, gaps, what to try next.\nRelated: [[Another paper]]", "#c2583a"),
        frame(830, 20, 460, 550, "Figures & sketches", "#8aa3bf"),
        note(870, 620, "Questions to ask", "#f3c0bb", "#d9b25a", 220, 0.02),
        sticker(1180, -120, "magnifier", 100, 0.12)
      ])
  },
  {
    key: "lecture",
    title: "Lecture notes",
    blurb: "Ruled paper and a margin",
    bg: { paper: "white", pattern: "lines", size: 24 },
    make: () => build([text(0, -80, "Lecture —", 48, "hand"), { ...shape(-22, -10, 3, 1400, "rect", "", "#e3a58a", "solid"), stroke: "#e3a58a", strokeWidth: 1, rough: 0 }, text(640, -70, "date", 24, "hand", "#8b7462")])
  },
  {
    key: "storyboard",
    title: "Storyboard",
    blurb: "Six panels with captions",
    bg: { paper: "white", pattern: "blank", size: 24 },
    make: () => {
      const specs: Spec[] = [];
      for (let i = 0; i < 6; i++) {
        const x = (i % 3) * 420;
        const y = Math.floor(i / 3) * 400;
        specs.push(frame(x, y, 380, 250, "Scene " + (i + 1), "#b7a28e"));
        specs.push(text(x + 4, y + 262, "caption…", 24, "hand", "#5b4636"));
      }
      return build(specs);
    }
  }
];

/** How a new notebook (a board of pages) is set up. */
export interface NotebookSpec {
  size: PageSizeKey;
  landscape: boolean;
  bg: BoardBackground;
}

export const DEFAULT_NOTEBOOK: NotebookSpec = { size: "letter", landscape: false, bg: { paper: "cream", pattern: "lines", size: 24 } };

/** Width and height of a notebook's pages. */
export function pageSize(spec: Pick<NotebookSpec, "size" | "landscape">): { w: number; h: number } {
  const s = PAGE_SIZES[spec.size] ?? PAGE_SIZES.letter;
  const long = Math.max(s.w, s.h);
  const short = Math.min(s.w, s.h);
  return spec.landscape ? { w: long, h: short } : { w: short, h: long };
}

/** The first page of a new notebook, centred on the origin's vertical axis. */
export function notebookPages(spec: NotebookSpec): PageElement[] {
  const { w, h } = pageSize(spec);
  return [{ id: newId(), type: "page", x: -w / 2, y: 0, w, h, rot: 0, z: "a0", t: Date.now() }];
}

export function templateByKey(key: string): Template | undefined {
  return TEMPLATES.find((t) => t.key === key);
}

/** A library preview for elements that haven't been opened on a board yet. */
export async function previewFor(els: SepiaElement[], bg: BoardBackground): Promise<string | null> {
  if (!els.length) return null;
  const [{ makeThumbnail }, { stickerUrl }] = await Promise.all([import("../editor/export"), import("./stickers")]);
  return makeThumbnail(els, bg, (s) => s, stickerUrl).catch(() => null);
}
